class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      if (this.isClearConfirmOpen()) {
        evt.preventDefault();
        this.hideClearConfirm();
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onClearCartClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this._lockScrollY = window.scrollY;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const alreadyOpen = this.classList.contains('active');
    setTimeout(() => {
      this.classList.add('animate', 'active');
      if (alreadyOpen) return;
      this.classList.add('d1-drawer-enter');
      clearTimeout(this._d1EnterTimer);
      this._d1EnterTimer = setTimeout(() => this.classList.remove('d1-drawer-enter'), 1200);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.setTawkVisible(false);
  }

  close() {
    const lockY = this._lockScrollY;
    const restore = this.focusTargetOnClose();
    clearTimeout(this._d1EnterTimer);
    this.classList.remove('active', 'd1-drawer-enter');
    removeTrapFocus();
    if (restore && typeof restore.focus === 'function') {
      restore.focus({ preventScroll: true });
    }
    document.body.classList.remove('overflow-hidden');
    if (typeof lockY === 'number') {
      window.scrollTo(0, lockY);
    }
    this.setTawkVisible(true);
  }

  focusTargetOnClose() {
    const el = this.activeElement;
    if (!el || !document.contains(el)) return null;
    if (el.id === 'cart-icon-bubble' || el.closest('#cart-icon-bubble')) return null;
    return el;
  }

  // ponytail: custom launcher is the bubble — cart only closes an open chat
  setTawkVisible(visible) {
    const apply = () => {
      try {
        if (visible) return;
        if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
          window.Tawk_API.hideWidget();
        }
      } catch (_) {}
    };
    apply();
    if (!visible) setTimeout(apply, 400);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /**
   * Never treat missing item_count as "has items" — that caused the zombie $0 checkout drawer.
   * @returns {boolean|null}
   */
  static inferEmpty(parsedState) {
    if (!parsedState) return true;
    if (typeof parsedState.item_count === 'number') return parsedState.item_count === 0;
    if (typeof parsedState.item_count === 'string' && parsedState.item_count !== '') {
      return Number(parsedState.item_count) === 0;
    }

    const html = parsedState.sections && parsedState.sections['cart-drawer'];
    if (typeof html === 'string') {
      if (html.includes('drawer__inner-empty')) return true;
      if (/<cart-drawer-items\b[^>]*\bis-empty\b/i.test(html)) return true;
      if (/<cart-drawer\b[^>]*\bis-empty\b/i.test(html)) return true;
      if (html.includes('drawer__cart-items-wrapper')) return false;
      return true;
    }

    return null;
  }

  syncEmptyState(empty) {
    if (typeof empty !== 'boolean') return;
    this.classList.toggle('is-empty', empty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', empty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.addEventListener('click', this.close.bind(this));
  }

  /**
   * @param {object} parsedState
   * @param {{ open?: boolean }} [options]
   */
  renderContents(parsedState, options = {}) {
    const openDrawer = options.open !== false;
    if (typeof window.__d1ZipPark === 'function') window.__d1ZipPark();

    this.productId = parsedState && parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState?.sections || !parsedState.sections[section.id]) return;
      try {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      } catch (e) {
        console.error('cart-drawer renderContents', section.id, e);
      }
    });

    const empty = CartDrawer.inferEmpty(parsedState);
    this.syncEmptyState(empty === null ? !this.querySelector('#CartDrawer-CartItems .cart-item') : empty);

    if (typeof window.__d1ZipRestore === 'function') {
      window.__d1ZipRestore(this.classList.contains('is-empty') ? 0 : parsedState?.total_price);
    }

    setTimeout(() => {
      this.bindOverlay();
      if (openDrawer) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  isClearConfirmOpen() {
    const dialog = this.querySelector('[data-d1-clear-cart-dialog]');
    return Boolean(dialog && !dialog.hidden);
  }

  showClearConfirm() {
    const dialog = this.querySelector('[data-d1-clear-cart-dialog]');
    if (!dialog) return;
    dialog.hidden = false;
    const cancel = dialog.querySelector('[data-d1-clear-cart-cancel]');
    if (cancel) cancel.focus();
  }

  hideClearConfirm() {
    this.querySelectorAll('[data-d1-clear-cart-dialog]').forEach((dialog) => {
      dialog.hidden = true;
    });
    const trigger = this.querySelector('[data-d1-clear-cart]');
    if (trigger) trigger.focus();
  }

  dismissClearConfirm() {
    this.querySelectorAll('[data-d1-clear-cart-dialog]').forEach((dialog) => dialog.remove());
  }

  onClearCartClick(event) {
    if (event.target.closest('[data-d1-clear-cart]')) {
      event.preventDefault();
      this.showClearConfirm();
      return;
    }
    if (event.target.closest('[data-d1-clear-cart-cancel]')) {
      event.preventDefault();
      this.hideClearConfirm();
      return;
    }
    if (event.target.matches('[data-d1-clear-cart-dialog]')) {
      event.preventDefault();
      this.hideClearConfirm();
      return;
    }
    if (event.target.closest('[data-d1-clear-cart-confirm]')) {
      event.preventDefault();
      this.clearCart();
    }
  }

  setClearBusy(busy) {
    const confirmBtn = this.querySelector('[data-d1-clear-cart-confirm]');
    const cancelBtn = this.querySelector('[data-d1-clear-cart-cancel]');
    if (confirmBtn) {
      confirmBtn.disabled = busy;
      if (busy) confirmBtn.setAttribute('aria-busy', 'true');
      else confirmBtn.removeAttribute('aria-busy');
    }
    if (cancelBtn) cancelBtn.disabled = busy;
  }

  refreshCartIconBubble() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return Promise.resolve();
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(`${cartUrl}?section_id=cart-icon-bubble`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          bubble.querySelector('.cart-count-bubble')?.remove();
          return;
        }
        return response.text().then((html) => {
          const inner = this.getSectionInnerHTML(html, '.shopify-section');
          if (inner) bubble.innerHTML = inner;
        });
      })
      .catch(() => {
        bubble.querySelector('.cart-count-bubble')?.remove();
      });
  }

  clearCart() {
    if (this._clearing) return;
    this._clearing = true;
    this.setClearBusy(true);

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const clearUrl = (window.routes && window.routes.cart_clear_url) || `${cartUrl}/clear.js`;

    fetch(clearUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('clear failed');
        return response.json().catch(() => ({ item_count: 0, items: [] }));
      })
      .then((cart) => {
        this.dismissClearConfirm();
        const items = this.querySelector('cart-drawer-items');
        const paintDrawer =
          items && typeof items.onCartUpdate === 'function'
            ? Promise.resolve(items.onCartUpdate())
            : Promise.resolve();

        return Promise.all([paintDrawer, this.refreshCartIconBubble()]).then(() => {
          this.dismissClearConfirm();
          this.syncEmptyState(!cart || Number(cart.item_count) === 0);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
          }
        });
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.setClearBusy(false);
      })
      .finally(() => {
        this._clearing = false;
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
